import { Directive, ElementRef, Input, OnInit, AfterViewInit, HostListener, OnDestroy } from "@angular/core";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TitlecaseForTextPipe } from "../pipes/titlecase-for-text.pipe";

@Directive({
    selector: "[autoTooltip]",
    standalone: false,
    hostDirectives: [
        {
            directive: NgbTooltip,
            inputs: ['placement', 'container']
        }
    ]
})
export class AutoTooltipDirective implements OnInit, AfterViewInit, OnDestroy {
    // Static WeakMap to track which panels already have scroll listeners attached
    // This prevents attaching duplicate listeners for each tooltip instance in the same panel
    private static panelListeners = new WeakMap<Element, boolean>();
    
    @Input() autoTooltip: string = '';
    private dropdownWidth: number = 300;
    private cachedNaturalWidth: number | null = null;
    private currentTooltipText: string = '';
    private openTimeout: any;
    titlecaseForText = new TitlecaseForTextPipe();
    
    constructor(
        private el: ElementRef,
        private ngbTooltip: NgbTooltip
    ) {
        // Set triggers to manual so we can control when tooltips show
        this.ngbTooltip.triggers = 'manual';
    }

    ngOnInit() {
        // Apply white background, black text, black border styling
        this.ngbTooltip.tooltipClass = 'dropdown-smart-tooltip';
        
        // Attach to body to ensure it's not constrained by parent containers
        this.ngbTooltip.container = 'body';

        // Get dropdown width early - use ng-select width, not scrollable-content
        const ngSelectElement = this.el.nativeElement.closest('ng-select');
        const dropdownPanel = this.el.nativeElement.closest('.ng-dropdown-panel');
        if (ngSelectElement) {
            // Use ng-select width, capped at 400px to prevent inflated scrollable-content width
            this.dropdownWidth = Math.min(ngSelectElement.offsetWidth || 300, 400);
        } else if (dropdownPanel) {
            this.dropdownWidth = Math.min(dropdownPanel.offsetWidth || 300, 400);
        }

        // Configure Popper to intelligently position tooltip
        this.ngbTooltip.popperOptions = (options: any) => {
            options.modifiers = [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 0] // Negative offset to position adjacent to dropdown border
                    }
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['left', 'top', 'bottom'],
                        padding: 10,
                        boundary: 'viewport',
                        rootBoundary: 'viewport'
                    }
                },
                {
                    name: 'preventOverflow',
                    options: {
                        padding: 10,
                        boundary: 'viewport',
                        rootBoundary: 'viewport',
                        tether: false,
                        altAxis: true
                    }
                },
                {
                    name: 'arrow',
                    options: {
                        padding: 5
                    }
                },
                {
                    name: 'applyWidth',
                    enabled: true,
                    phase: 'beforeWrite',
                    fn: ({ state }: any) => {
                        if (state.elements.popper) {
                            const tooltipInner = state.elements.popper.querySelector('.tooltip-inner');
                            if (tooltipInner && this.dropdownWidth) {
                                const tooltipText = tooltipInner.textContent || '';
                                
                                // Cache measurement if text hasn't changed
                                if (tooltipText !== this.currentTooltipText) {
                                    this.currentTooltipText = tooltipText;
                                    
                                    // Create a hidden canvas for text measurement (no DOM manipulation)
                                    const canvas = document.createElement('canvas');
                                    const context = canvas.getContext('2d');
                                    if (context) {
                                        // Match tooltip styling for accurate measurement
                                        context.font = '0.9rem -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
                                        const textMetrics = context.measureText(tooltipText);
                                        // Add padding (8px * 2) + border (1px * 2) + buffer (10px)
                                        this.cachedNaturalWidth = Math.ceil(textMetrics.width) + 38;
                                    } else {
                                        // Fallback to rough estimate if canvas not available
                                        this.cachedNaturalWidth = tooltipText.length * 8 + 38;
                                    }
                                }
                                
                                // Get viewport width
                                const viewportWidth = window.innerWidth - 20; // Leave 10px margin on each side
                                
                                // Only constrain width if text exceeds dropdown width
                                if (this.cachedNaturalWidth && this.cachedNaturalWidth > this.dropdownWidth) {
                                    // Use dropdown width but ensure it doesn't exceed viewport
                                    const constrainedWidth = Math.min(this.dropdownWidth, viewportWidth);
                                    tooltipInner.style.width = `${constrainedWidth}px`;
                                    tooltipInner.style.maxWidth = `${constrainedWidth}px`;
                                    tooltipInner.style.minWidth = 'auto';
                                } else {
                                    // Auto-size for short text, but ensure it fits in viewport
                                    tooltipInner.style.width = 'auto';
                                    tooltipInner.style.maxWidth = `${Math.min(this.cachedNaturalWidth || 300, viewportWidth)}px`;
                                    tooltipInner.style.minWidth = 'auto';
                                }
                                tooltipInner.style.whiteSpace = 'normal';
                                tooltipInner.style.wordWrap = 'break-word';
                                tooltipInner.style.overflowWrap = 'break-word';
                                tooltipInner.style.wordBreak = 'break-word';
                            }
                        }
                    }
                }
            ];
            
            return options;
        };
    }

    ngAfterViewInit() {
        // Set tooltip content immediately
        this.updateTooltip();
        
        // Set up scroll listener to hide tooltip when dropdown scrolls
        this.setupScrollListener();
    }
    
    private setupScrollListener() {
        const dropdownPanel = this.el.nativeElement.closest('.ng-dropdown-panel');
        if (!dropdownPanel) {
            // Try body-appended panels
            const bodyPanels = document.querySelectorAll('body > .ng-dropdown-panel');
            bodyPanels.forEach(panel => this.attachScrollListenersToPanel(panel as Element));
            return;
        }
        
        this.attachScrollListenersToPanel(dropdownPanel);
    }
    
    private attachScrollListenersToPanel(panel: Element) {
        // Check if this panel already has listeners attached (prevents duplicate listeners)
        if (AutoTooltipDirective.panelListeners.has(panel)) {
            return; // Early return for performance - listeners already exist
        }
        
        // Mark this panel as having listeners
        AutoTooltipDirective.panelListeners.set(panel, true);
        
        // Add mouseenter listener to SET the data attribute (CSS handles the hiding)
        const panelMouseEnterListener = () => {
            // Set global flag - CSS attribute selector will hide tooltips instantly
            document.body.setAttribute('data-hovering-option', 'true');
            
            // Close any existing ng-select value tooltips (one-time, for already-visible tooltips)
            // Scoped to only ng-select tooltips to avoid affecting unrelated tooltips
            const ngSelectTooltips = document.querySelectorAll('.ng-select-value-tooltip');
            if (ngSelectTooltips?.length > 0) {
                // Convert NodeList to Array for iteration
                for (const tooltip of Array.from(ngSelectTooltips)) {
                    (tooltip as HTMLElement).style.display = 'none';
                }
            }
        };
        
        // Add mouseleave listener to REMOVE the data attribute
        const panelMouseLeaveListener = () => {
            this.cleanupGlobalTooltipState();
        };
        
        // Use passive listeners for better scroll performance
        panel.addEventListener('mouseenter', panelMouseEnterListener, { passive: true });
        panel.addEventListener('mouseleave', panelMouseLeaveListener, { passive: true });
        
        const listener = () => {
            // Close all option tooltips when scrolling (performance: single query + minimal loop)
            const tooltips = document.querySelectorAll('.dropdown-smart-tooltip');
            if (tooltips?.length > 0) {
                for (const tooltip of Array.from(tooltips)) {
                    (tooltip as HTMLElement).style.display = 'none';
                }
            }
        };
        
        const keyListener = (e: Event) => {
            const keyEvent = e as KeyboardEvent;
            // Arrow keys, Page Up/Down, Home/End cause scrolling
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(keyEvent.key)) {
                listener();
            }
        };
        
        // Find ALL possible scroll containers and attach listeners to each
        const scrollContainers = [
            panel.querySelector('.ng-dropdown-panel-items'),
            panel.querySelector('.scroll-host'),
            panel.querySelector('.scrollable-content'),
            panel // The panel itself might scroll
        ];
        
        scrollContainers.forEach(container => {
            if (container) {
                // Use capture phase to catch scroll event as early as possible
                // Listen to multiple events for comprehensive scroll detection
                // Note: These are attached once per panel, not per tooltip instance
                container.addEventListener('scroll', listener, { capture: true, passive: true });
                
                container.addEventListener('wheel', listener, { capture: true, passive: true });
                
                container.addEventListener('touchmove', listener, { capture: true, passive: true });
                
                container.addEventListener('keydown', keyListener, { capture: true });
            }
        });
    }
    
    ngOnDestroy() {
        // Clear any pending timeouts
        if (this.openTimeout) {
            clearTimeout(this.openTimeout);
        }
        
        // Ensure tooltip is closed
        this.ngbTooltip.close();
        
        // DEFENSIVE CLEANUP: Remove data-hovering-option attribute if set
        // This handles the case where the directive is destroyed (dropdown closed via click)
        // before mouseleave fires, preventing the attribute from getting stuck on body
        this.cleanupGlobalTooltipState();
        
        // Note: We don't remove scroll listeners here because they're shared across all instances
        // in the same panel. The listeners will be garbage collected when the panel is removed.
    }

    @HostListener('mouseenter')
    onMouseEnter() {
        // Clear any pending open timeout to prevent multiple tooltips
        if (this.openTimeout) {
            clearTimeout(this.openTimeout);
        }
        
        // Set the data attribute to prevent selected value tooltip from showing
        // CSS attribute selector will handle hiding instantly - this is the most performant approach
        document.body.setAttribute('data-hovering-option', 'true');
        
        // Hide any existing ng-select value tooltip DOM elements (only if they exist - minimal DOM query)
        // Scoped to only ng-select tooltips to avoid affecting unrelated tooltips
        const existingNgSelectTooltips = document.querySelectorAll('.ng-select-value-tooltip');
        if (existingNgSelectTooltips?.length > 0) {
            // Convert NodeList to Array for iteration
            for (const tooltip of Array.from(existingNgSelectTooltips)) {
                (tooltip as HTMLElement).style.display = 'none';
            }
        }
        
        // Ensure scroll listeners are set up (cached via WeakMap - no duplicate listeners)
        // This ensures panel mouseleave listener is attached to remove the attribute when leaving panel
        this.setupScrollListener();
        
        // Check if this is the first column in a multi-column dropdown
        // If so, don't show option tooltip but keep attribute set to prevent selected value tooltip too
        if (this.isFirstColumnInMultiColumnDropdown()) {
            return;
        }
        
        // Debounce tooltip opening to prevent overlapping when hovering quickly
        this.openTimeout = setTimeout(() => {
            // Update dropdown width on each hover - use ng-select width, capped at 400px
            const MAX_DROPDOWN_WIDTH = 400;
            const ngSelectElement = this.el.nativeElement.closest('ng-select');
            const dropdownPanel = this.el.nativeElement.closest('.ng-dropdown-panel');
            if (ngSelectElement) {
                this.dropdownWidth = Math.min(ngSelectElement.offsetWidth || 300, MAX_DROPDOWN_WIDTH);
            } else if (dropdownPanel) {
                this.dropdownWidth = Math.min(dropdownPanel.offsetWidth || 300, MAX_DROPDOWN_WIDTH);
        }
        
        // Use dropdown panel position for placement detection, not option element
        const positionElement = dropdownPanel || ngSelectElement || this.el.nativeElement;
        const rect = positionElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate available space in all directions
        const spaceTop = rect.top;
        const spaceBottom = viewportHeight - rect.bottom;
        const spaceRight = viewportWidth - rect.right;
        const spaceLeft = rect.left;
        
        // Estimated tooltip height (approximate based on text wrapping)
        const estimatedTooltipHeight = 100; // Conservative estimate for wrapped text
        const neededHorizontalSpace = this.dropdownWidth + 20;
        
        // Priority 1: Try top or bottom first
        if (spaceTop >= estimatedTooltipHeight) {
            this.ngbTooltip.placement = 'top';
        } else if (spaceBottom >= estimatedTooltipHeight) {
            this.ngbTooltip.placement = 'bottom';
        }
        // Priority 2: If no vertical space, use left or right
        else if (spaceRight >= neededHorizontalSpace) {
            this.ngbTooltip.placement = 'right';
        } else if (spaceLeft >= neededHorizontalSpace) {
            this.ngbTooltip.placement = 'left';
        } else {
            // Final fallback: use bottom even if cramped
            this.ngbTooltip.placement = 'bottom';
        }
        
        this.updateTooltip();
        
        // Reset CSS properties that might have been set during scroll hiding
        this.ngbTooltip.open();
        setTimeout(() => {
            const tooltipElement = document.querySelector('.dropdown-smart-tooltip');
            if (tooltipElement) {
                (tooltipElement as HTMLElement).style.display = '';
                (tooltipElement as HTMLElement).style.visibility = '';
                (tooltipElement as HTMLElement).style.opacity = '';
            }
        }, 0);
        }, 200); // 200ms delay before opening tooltip
    }
    
    @HostListener('mouseleave')
    onMouseLeave() {
        // Cancel any pending tooltip open
        if (this.openTimeout) {
            clearTimeout(this.openTimeout);
        }
        
        // Close tooltip immediately
        this.ngbTooltip.close();
    }

    /**
     * Cleanup global tooltip state to prevent tooltips from remaining disabled
     * Removes the data-hovering-option attribute and clears inline display styles
     * Scoped to only ng-select value tooltips to avoid affecting unrelated tooltips
     */
    private cleanupGlobalTooltipState(): void {
        // Remove the body attribute that prevents selected value tooltips from showing
        if (document.body.hasAttribute('data-hovering-option')) {
            document.body.removeAttribute('data-hovering-option');
        }
        
        // Clear any inline display styles we set on ng-select value tooltips
        // Scoped to only ng-select tooltips to avoid affecting unrelated tooltips
        const ngSelectTooltips = document.querySelectorAll('.ng-select-value-tooltip');
        if (ngSelectTooltips?.length > 0) {
            for (const tooltip of Array.from(ngSelectTooltips)) {
                (tooltip as HTMLElement).style.display = '';
            }
        }
    }

    private isFirstColumnInMultiColumnDropdown(): boolean {
        const element = this.el.nativeElement;
        
        // Check both ng-select element and dropdown panel for multi-column classes
        const ngSelectElement = element.closest('ng-select');
        const dropdownPanel = element.closest('.ng-dropdown-panel');
        
        let isDoubleColumn = false;
        
        // Check ng-select element for multi-column classes (without -panel suffix)
        // This works for both inline and body-appended dropdowns
        if (ngSelectElement) {
            isDoubleColumn = ngSelectElement.classList.contains('multi-column-dropdown-2') ||
                             ngSelectElement.classList.contains('multi-column-dropdown-2-wide');
        }
        
        // Also check dropdown panel for multi-column panel classes (with -panel suffix)
        // This handles body-appended panels or cases where ng-select is not found
        if (!isDoubleColumn && dropdownPanel) {
            isDoubleColumn = dropdownPanel.classList.contains('multi-column-dropdown-2-panel') ||
                             dropdownPanel.classList.contains('multi-column-dropdown-2-wide-panel');
        }
        
        // For body-appended panels, also check if there's an ng-select with multi-column class
        // by looking for the closest ng-select in the document (not just ancestors)
        if (!isDoubleColumn && dropdownPanel?.classList.contains('appended-to-body')) {
            // Find the open ng-select with multi-column dropdown class
            const openNgSelects = document.querySelectorAll('ng-select.ng-select-opened');
            for (const select of Array.from(openNgSelects)) {
                if (select.classList.contains('multi-column-dropdown-2') ||
                    select.classList.contains('multi-column-dropdown-2-wide')) {
                    isDoubleColumn = true;
                    break;
                }
            }
        }
        
        if (!isDoubleColumn) return false;
        
        // Check if this element is the first column in its row
        const parentRow = element.closest('.row, .padding_0_1_rem');
        if (!parentRow) return false;
        
        // Find all column elements in the row
        const columns = Array.from(parentRow.querySelectorAll('.custom--select__dataflow'));
        
        // Check if this is the first column (index 0)
        return columns.length > 1 && columns[0] === element;
    }

    private updateTooltip() {
        const element = this.el.nativeElement;
        
        // Get tooltip text from input; only fall back to element content if input is null/undefined
        let tooltipText = this.autoTooltip;
        if (tooltipText == null) {
            const rawText = element.textContent ?? element.innerText ?? '';
            tooltipText = rawText.trim();
        }
        const text = tooltipText ? String(tooltipText).trim() : '';

        if (text) {
            const normalized = text
                .replace(/\s*,\s*/g, ', ')
                .replace(/\s+/g, ' ');

            this.ngbTooltip.ngbTooltip = this.titlecaseForText.transform(normalized);
            this.ngbTooltip.disableTooltip = false;
        } else {
            this.ngbTooltip.ngbTooltip = '';
            this.ngbTooltip.disableTooltip = true;
        }
    }
}


